import { useColorContext } from "@/styles/themes/default";
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";

import { useForm } from "react-hook-form";
import { FaAnglesRight, FaTelegram } from "react-icons/fa6";
import { GoTrophy } from "react-icons/go";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { MdOutlineAnalytics } from "react-icons/md";

import * as S from "./styles";
import { leaguesData } from "./data";
import { useNavigate } from "react-router-dom";
import { FutButton } from "@/components/FutButton";
import FutDatePicker from "@/components/FutDatePicker";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import tabLine from "@/assets/dashboard-home/tab-line.png";
import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

// import required modules
import { Pagination } from "swiper/modules";

import { MatchData, OddsData } from "@/interfaces/liveGamesType";
import { toast } from "react-toastify";
import { format, addDays, subDays } from "date-fns";
import { catchError } from "@/utils/catchError";
import { ObjectValues, StringToNumber } from "@/@types/customTypes";
import { BiFilterAlt } from "react-icons/bi";
import RoundInput from "@/components/RoundInput";
import { FaSearch } from "react-icons/fa";
import { translatedStats } from "@/constants/footballStats";
import useGameData from "@/hooks/useGameData";
import AttackMomentum from "@/components/AttackMomentum";
import WhoWins from "@/components/WhoWins";
import {
  fetchHistoryMatches,
  fetchLiveMatches,
  fetchPredictions,
} from "@/hooks/fetchHooks/homeHooks";
import checkSameDay from "@/utils/checkSameDay";
import Matches from "./components/Matches";
import { useInView } from "react-intersection-observer";
import { useAuth } from "@/context/AuthContext";
import { PiTelegramLogo } from "react-icons/pi";
interface futFormType {
  isLiveMatch: boolean;
  datePicker: Date;
  searchMatch: string;
}

interface IGameAnalysis extends MatchData {
  iframeId?: number;
  matchOdds: StringToNumber<OddsData>;
}

interface IGameData extends MatchData {
  loading?: boolean;
}

const tabsEnum = {
  HISTORY_MATCHES: "HISTORY_MATCHES",
  LIVE_MATCHES: "LIVE_MATCHES",
  PREDICTIONS: "PREDICTIONS",
} as const;

type tabsEnumType = ObjectValues<typeof tabsEnum>;

export default function Home() {
  const { toggleColorMode } = useColorContext();
  const { getMatchStats } = useGameData();

  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isLeaguesOpen, setIsLeaguesOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const tabs = useRef<tabsEnumType>(tabsEnum.HISTORY_MATCHES);
  const [predictionDate, setPredictionDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );
  const [historyDate, setHistoryDate] = useState(
    format(subDays(new Date(), 1), "yyyy-MM-dd")
  );

  const [filteredData, setFilteredData] = useState<IGameData[] | undefined>([]);
  const [selectedLeague, setSelectedLeague] = useState<undefined | RegExp>(
    undefined
  );
  const [search, setSearch] = useState("");
  const [openMobileModal, setOpenMobileModal] = useState(false);

  const [gameAnalysis, setGameAnalysis] = useState<IGameAnalysis[]>([]);

  const PAGE_SIZE = 40;
  const theme = useTheme();
  const navigate = useNavigate();
  const isMedia1200 = !useMediaQuery(theme.breakpoints.up("lg"));
  const { decodedToken } = useAuth();

  const getPrediction = fetchPredictions(
    {
      time: predictionDate,
      pageSize: PAGE_SIZE,
      ligas: selectedLeague?.source,
      search,
    },
    {
      meta: { errorMessage: "Ocorreu um erro ao tentar buscar as previsões" },
      refetchInterval: 60000,
    }
  );

  const getHistoryMatches = fetchHistoryMatches(
    {
      time: historyDate,
      pageSize: PAGE_SIZE,
      ligas: selectedLeague?.source,
      search,
    },
    {
      meta: {
        errorMessage: "Ocorreu um erro ao tentar buscar as partidas passadas",
      },
    }
  );

  const getLiveMatches = fetchLiveMatches(setGameAnalysis, {
    meta: {
      errorMessage: "Ocorreu um erro ao tentar buscar as partidas ao vivo",
    },
    refetchInterval: 60000,
  });

  const cardRef = useRef<Array<HTMLDivElement>>([]);

  const futForm = useForm<futFormType>({
    defaultValues: {
      datePicker: new Date(),
      isLiveMatch: false,
      searchMatch: "",
    },
  });

  const watchSearchMatch = futForm.watch("searchMatch");

  const { ref, inView } = useInView({});

  useEffect(() => {
    if (inView) {
      if (tabs.current === "HISTORY_MATCHES" && getHistoryMatches.hasNextPage) {
        getHistoryMatches.fetchNextPage();
      }

      if (tabs.current === "PREDICTIONS" && getPrediction.hasNextPage) {
        getPrediction.fetchNextPage();
      }
    }
  }, [inView]);

  const changeTabs = (event: React.SyntheticEvent, newValue: tabsEnumType) => {
    tabs.current = newValue;

    setFilteredData([]);
    setSelectedLeague(undefined);
  };

  const getGameHistory = async () => {
    try {
      setIsLoading(true);
      const getPicker = format(futForm.getValues("datePicker"), "yyyy-MM-dd");

      setHistoryDate(getPicker);

      setIsLoading(false);
    } catch (e) {
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error(
        "ocorreu um erro inesperado ao tentar carregar os jogos passados"
      );
    }
  };

  const filterGames = async (leagueRegex: RegExp | undefined) => {
    if (!leagueRegex?.source) {
      setSelectedLeague(undefined);
      setFilteredData([]);
      return;
    }
    futForm.setValue("searchMatch", "");
    setFilteredData([]);
    setSelectedLeague(leagueRegex);

    if (tabs.current === "LIVE_MATCHES") {
      const filtered = getLiveMatches.data?.filter((el) =>
        leagueRegex.test(el.league_name.toLocaleLowerCase())
      );

      setFilteredData(filtered);
      return;
    }
  };

  const stateController = (str: "analysis" | "leagues") => {
    if (str === "analysis") {
      setIsAnalyticsOpen((prev) => !prev);
    }
    if (str === "leagues") {
      setIsLeaguesOpen((prev) => !prev);
    }
  };
  //TODO: Pensar em uma forma de refatorar grabStatistics de uma forma mais bonita
  const grabStatistics = async (gameObj: MatchData) => {
    const isInfiniteQuery =
      tabs.current === "PREDICTIONS" || tabs.current === "HISTORY_MATCHES";

    if (isInfiniteQuery) {
      const infiniteQueryData =
        tabs.current === "HISTORY_MATCHES"
          ? getHistoryMatches.data?.pages
          : getPrediction.data?.pages;

      const firstGameIndex = infiniteQueryData?.findIndex((item) =>
        item.some((match) => match.match_id === gameObj.match_id)
      )!;

      const secondGameIndex = infiniteQueryData![firstGameIndex!].findIndex(
        (el) => el.match_id === gameObj.match_id
      );

      const setMatchLoading = (value: boolean) => {
        if (firstGameIndex && secondGameIndex && infiniteQueryData) {
          infiniteQueryData[firstGameIndex][secondGameIndex] = {
            ...infiniteQueryData[firstGameIndex][secondGameIndex],
            loading: value,
          };
        }
      };

      setMatchLoading(true);
      const matchOdds: StringToNumber<OddsData> = await getMatchStats(
        gameObj.match_id
      );

      const gameToGrab = infiniteQueryData![firstGameIndex].find(
        (item) => item.match_id === gameObj.match_id
      )!;

      setGameAnalysis((prev) => {
        const gameAlreadyAdded = prev.find(
          (el) => el.match_id === gameObj.match_id
        );

        if (!gameAlreadyAdded) {
          return [...prev, { ...gameToGrab, matchOdds }];
        }

        return prev;
      });

      setMatchLoading(false);
      return;
    }

    //=====================================================

    const gameToGrab = getLiveMatches.data?.find(
      (el) => el.match_id === gameObj.match_id
    )!;

    const gameIndex = getLiveMatches.data?.findIndex(
      (el) => el.match_id === gameObj.match_id
    );

    const setMatchLoading = (value: boolean) => {
      if (getLiveMatches.data && gameIndex !== undefined) {
        getLiveMatches.data[gameIndex] = {
          ...getLiveMatches.data[gameIndex],
          loading: value,
        };
      }
    };

    setMatchLoading(true);
    const matchOdds: StringToNumber<OddsData> = await getMatchStats(
      gameObj.match_id
    );

    setGameAnalysis((prev) => {
      const gameAlreadyAdded = prev.find(
        (el) => el.match_id === gameObj.match_id
      );

      if (!gameAlreadyAdded) {
        return [...prev, { ...gameToGrab, matchOdds }];
      }

      return prev;
    });

    setMatchLoading(false);
  };

  const findGamesMatches = () => {
    const query = futForm.getValues("searchMatch");
    if (!query) {
      if (selectedLeague?.source) {
        filterGames(selectedLeague);
        setOpenMobileModal(false);
        return;
      }

      setSearch("")
      setOpenMobileModal(false);
      return;
    }

    const isInfiniteQuery =
      tabs.current === "PREDICTIONS" || tabs.current === "HISTORY_MATCHES";

    if (isInfiniteQuery) {
      setSearch(query)
    }

    if (tabs.current === "LIVE_MATCHES") {
      const filteredGames = getLiveMatches.data?.filter(
        (obj) =>
          obj.match_awayteam_name.toLowerCase().includes(query.toLowerCase()) ||
          obj.match_hometeam_name.toLowerCase().includes(query.toLowerCase()) ||
          obj.country_name.toLowerCase().includes(query.toLowerCase()) ||
          obj.match_time.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredData(filteredGames);
    }

    setOpenMobileModal(false);
  };

  const getPreMatches = async () => {
    try {
      const getPicker = format(futForm.getValues("datePicker"), "yyyy-MM-dd");

      setPredictionDate(getPicker);
    } catch (e) {
      toast.error("Ocorreu um erro ao tentar pesquisar as previsões");
    }
  };

  const showMoreHandler = async () => {
    try {
      if (tabs.current === "HISTORY_MATCHES") {
        getHistoryMatches.fetchNextPage();
      }

      if (tabs.current === "PREDICTIONS") {
        getPrediction.fetchNextPage();
      }
    } catch (e) {
      toast.error("Ocorreu um erro ao tentar carregar mais jogos");
    }
  };

  return (
    <S.HomeContainer>
      <S.FakeNavButton onClick={() => stateController("leagues")}>
        <S.MobileTrophyButton>
          <GoTrophy />
        </S.MobileTrophyButton>
      </S.FakeNavButton>

      <S.MobileBtnAnalysis onClick={() => stateController("analysis")}>
        <MdOutlineAnalytics />
      </S.MobileBtnAnalysis>
      <S.GameAnalysis isOpen={isAnalyticsOpen}>
        <S.GameHeader onClick={() => stateController("analysis")}>
      
          <h2>Analise de jogos</h2>
          {isAnalyticsOpen ? <AiOutlineDown /> : <AiOutlineUp />}
        </S.GameHeader>
        <S.GameContent>
          {gameAnalysis.length ? (
            gameAnalysis.map((el, i) => (
              <S.GameCard
                key={el.match_id}
                ref={(element) => (cardRef.current[i] = element!)}
              >
                <S.CloseGame>
                  <span
                    onClick={() => {
                      const gameIndex = gameAnalysis.findIndex(
                        (element) => element.match_id === el.match_id
                      );

                      const prevGameAnalysis = [...gameAnalysis];
                      prevGameAnalysis.splice(gameIndex, 1);

                      if (cardRef.current[i]) {
                        cardRef.current[i].style.transform =
                          "translateX(-110%)";
                      }

                      setTimeout(() => {
                        setGameAnalysis(prevGameAnalysis);
                        if (cardRef.current[i]) {
                          cardRef.current[i].style.transform = "translateX(0%)";
                        }
                      }, 300);
                    }}
                  >
                    X
                  </span>
                </S.CloseGame>
                {el.match_status === "Finished" ? (
                  <S.GameEndContainer>
                    <span>Jogo Finalizado</span>
                  </S.GameEndContainer>
                ) : (
                  <></>
                )}
                <S.GameInfoLive>
                  <S.TeamContainer>
                    <img src={el.team_home_badge} />
                    <p>{el.match_hometeam_name}</p>
                  </S.TeamContainer>
                  <S.GameScore>
                    <h3>
                      {el.match_hometeam_score} : {el.match_awayteam_score}
                    </h3>
                    <small>{el.match_time}</small>
                  </S.GameScore>
                  <S.TeamContainer>
                    <img src={el.team_away_badge} />
                    <p>{el.match_awayteam_name}</p>
                  </S.TeamContainer>
                </S.GameInfoLive>
                <S.GameStatisticsContainer>
                  {el.statistics?.length ? (
                    el.statistics.map((stats) => (
                      <S.GameStatistic
                        key={stats.type}
                        homePoints={parseInt(stats.home)}
                        awayPoints={parseInt(stats.away)}
                      >
                        <span>{stats.home}</span>{" "}
                        {
                          translatedStats[
                            stats.type as keyof typeof translatedStats
                          ]
                        }
                        <span>{stats.away}</span>
                      </S.GameStatistic>
                    ))
                  ) : (
                    <S.StatsNotFound>
                      <p>Estatísticas Não disponíveis</p>
                    </S.StatsNotFound>
                  )}
                  {futForm.getValues("datePicker").getTime() >
                  new Date().getTime() ? (
                    ""
                  ) : (
                    <AttackMomentum
                      matchData={el}
                      isFinished={el.match_status === "Finished"}
                    />
                  )}

                  {Object.values(el.matchOdds).reduce((a, b) => a + b, 0) ===
                  0 ? (
                    <S.PercentageContainer>
                      <S.StatsNotFound>
                        <p>Estatísticas Não disponíveis</p>
                      </S.StatsNotFound>
                    </S.PercentageContainer>
                  ) : (
                    <div>
                      <WhoWins
                        homeTeamPercentage={el.matchOdds.homeTeamWin}
                        awayTeamPercentage={el.matchOdds.awayTeamWin}
                        draw={el.matchOdds.draw}
                      />
                      <S.OtherOddsContainer>
                        <tbody>
                          <tr>
                            <th>
                              Dupla Chance - Vitória da Equipe da Casa ou Empate{" "}
                            </th>
                            <td>
                              {el.matchOdds.doubleChanceHomeTeamWinOrDraw}%
                            </td>
                          </tr>
                          <tr>
                            <th>
                              Dupla Chance - Vitória da Equipe Visitante ou
                              Empate{" "}
                            </th>
                            <td>
                              {el.matchOdds.doubleChanceAwayTeamWinOrDraw}%
                            </td>
                          </tr>
                          <tr>
                            <th>
                              Vitória da Equipe da Casa ou da Equipe Visitante{" "}
                            </th>
                            <td>{el.matchOdds.homeTeamOrAwayTeamToWin}%</td>
                          </tr>
                          <tr>
                            <th>Mais de 2,5 Gols no Jogo </th>
                            <td>{el.matchOdds.over2point5GoalsPerMatch}%</td>
                          </tr>
                          <tr>
                            <th>Menos de 2,5 Gols no Jogo </th>
                            <td>{el.matchOdds.under2point5GoalsPerMatch}%</td>
                          </tr>
                          <tr>
                            <th>Mais de 1,5 Gols no Jogo </th>
                            <td>{el.matchOdds.over1point5GoalsPerMatch}%</td>
                          </tr>
                          <tr>
                            <th>Menos de 1,5 Gols no Jogo </th>
                            <td>{el.matchOdds.under1point5GoalsPerMatch}%</td>
                          </tr>
                          <tr>
                            <th>Mais de 3,5 Gols no Jogo </th>
                            <td>{el.matchOdds.over3point5GoalsPerMatch}%</td>
                          </tr>
                          <tr>
                            <th>Menos de 3,5 Gols no Jogo </th>
                            <td>{el.matchOdds.under3point5GoalsPerMatch}%</td>
                          </tr>
                          <tr>
                            <th>Ambas as Equipes Marcam </th>
                            <td>{el.matchOdds.bothTeamToScore}%</td>
                          </tr>
                          <tr>
                            <th>Apenas Uma Equipe Marca </th>
                            <td>{el.matchOdds.onlyOneTeamToScore}%</td>
                          </tr>
                        </tbody>
                      </S.OtherOddsContainer>
                    </div>
                  )}
                </S.GameStatisticsContainer>
              </S.GameCard>
            ))
          ) : (
            <S.GameAnalyticsNotFound>
              <p>
                Clique em{" "}
                <span>
                  <MdOutlineAnalytics /> Acompanhar estatísticas
                </span>{" "}
                para ver informações mais detalhadas
              </p>
            </S.GameAnalyticsNotFound>
          )}
        </S.GameContent>
      </S.GameAnalysis>

      <Grid container>
        <Grid
          item
          sm={2}
          paddingTop={isMedia1200 ? 0 : 3}
          display="flex"
          alignItems="flex-start"
        >
          <S.LeagueContainer isOpen={isLeaguesOpen}>
            <h2>
              Ligas <span onClick={() => stateController("leagues")}>X</span>
            </h2>
            {leaguesData.map((el) => (
              <a
                className={
                  el.leagueRegex?.source === selectedLeague?.source
                    ? "active"
                    : ""
                }
                onClick={() => {
                  filterGames(el.leagueRegex);
                }}
                key={el.text}
              >
                <div>
                  <img src={el.img} />
                  <span>{el.text}</span>
                </div>
              </a>
            ))}
          </S.LeagueContainer>
        </Grid>
        <Grid
          item
          sm={isMedia1200 ? 12 : 10}
          padding={isMedia1200 ? 0 : 3}
          maxWidth="100%"
        >
          <Grid container>
            {isMedia1200 ? (
              <Swiper pagination modules={[Pagination]}>
                {/* <SwiperSlide>
                  <S.GptAnalysis>
                    <S.GptCover />
                    <h2>{textH2Telegram}</h2>
                    <p>{textPTelegram}</p>
                    <S.TelegramButton
                      sx={{ mt: 3, width: "150px" }}
                      onClick={() => window.open(telegramLink)}
                    >
                      <PiTelegramLogo size={20} /> Acessar Agora
                    </S.TelegramButton>
                  </S.GptAnalysis>
                </SwiperSlide>
                <SwiperSlide>
                  <S.ReferFriendsContainer>
                    <h2>Indique amigos e ganhe dinheiro!</h2>
                    <p>
                      Participe do sistema de indicações para se tornar um
                      verdadeiro campeão.
                    </p>
                    <FutButton
                      sx={{ mt: 3 }}
                      onClick={() => navigate("/dashboard/afiliado")}
                      variant="outlined"
                    >
                      &nbsp;Começar agora&nbsp; <FaAnglesRight />
                      &nbsp;
                    </FutButton>
                  </S.ReferFriendsContainer>
                </SwiperSlide> */}
              </Swiper>
            ) : (
              <>
                {/* <Grid item sm={6}>
                  <S.GptAnalysis>
                    <S.GptCover />
                    <h2>{textH2Telegram}</h2>
                    <p>{textPTelegram}</p>
                    <S.TelegramButton
                      sx={{ mt: 3, width: "150px" }}
                      onClick={() => window.open(telegramLink)}
                    >
                      <PiTelegramLogo size={20} /> Acessar Agora
                    </S.TelegramButton>
                  </S.GptAnalysis>
                </Grid>
                <Grid item sm={6}>
                  <S.ReferFriendsContainer>
                    <h2>Indique amigos e ganhe dinheiro!</h2>
                    <p>
                      Participe do sistema de indicações para se tornar um
                      verdadeiro campeão.
                    </p>
                    <FutButton
                      sx={{ mt: 3 }}
                      onClick={() => navigate("/dashboard/afiliado")}
                      variant="outlined"
                    >
                      &nbsp;Começar agora&nbsp; <FaAnglesRight />
                      &nbsp;
                    </FutButton>
                  </S.ReferFriendsContainer>
                </Grid> */}
              </>
            )}
          </Grid>
          <TabContext value={tabs.current}>
            <S.GameFilterContainer>
              <S.ContainerTabs>
                <TabList
                  onChange={changeTabs}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ width: "100%" }}
                >
                  <Tab
                    label="Jogos Passados"
                    value={tabsEnum.HISTORY_MATCHES}
                  />
                  <Tab label="Partidas Ao Vivo" value={tabsEnum.LIVE_MATCHES} />
                  <Tab label="Previsões" value={tabsEnum.PREDICTIONS} />
                </TabList>
              </S.ContainerTabs>

              <S.FilterContainer>
                <S.FilterMobileBtn
                  onClick={() => setOpenMobileModal((prev) => !prev)}
                >
                  <p>
                    Filtrar <BiFilterAlt />
                  </p>
                </S.FilterMobileBtn>
              </S.FilterContainer>

              <Modal
                open={openMobileModal}
                onClose={() => setOpenMobileModal(false)}
              >
                <S.ModalMobileContainer>
                  <S.ModalMobileContent>
                    <h2>
                      Filtros
                      <span onClick={() => setOpenMobileModal(false)}>X</span>
                    </h2>
                    <p>Data do Jogos</p>
                    <FutDatePicker
                      control={futForm.control}
                      minDate={new Date("2000-01-02")}
                      inputName="datePicker"
                      onAccept={() => {
                        if (tabs.current === "PREDICTIONS") {
                          getPreMatches();
                          return;
                        }
                        getGameHistory();
                      }}
                      disablePast={tabs.current === "PREDICTIONS"}
                      disableFuture={tabs.current === "HISTORY_MATCHES"}
                      disabled={tabs.current === "LIVE_MATCHES"}
                    />

                    <p>Pesquisar Por Time</p>

                    <RoundInput
                      inputName="searchMatch"
                      control={futForm.control}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") {
                          findGamesMatches();
                        }
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          {watchSearchMatch ? (
                            <IconButton
                              onClick={() => {
                                futForm.setValue("searchMatch", "");
                              }}
                              edge="end"
                            >
                              <IoIosCloseCircleOutline />
                            </IconButton>
                          ) : (
                            <FaSearch />
                          )}
                        </InputAdornment>
                      }
                      fullWidth
                      size="small"
                      autoComplete="email"
                    />

                    <FutButton sx={{ px: 3, mt: 2 }} onClick={findGamesMatches}>
                      Aplicar Filtro
                    </FutButton>
                  </S.ModalMobileContent>
                </S.ModalMobileContainer>
              </Modal>
            </S.GameFilterContainer>

            <S.GameBodyContainer>
              <h2>Jogos</h2>
              {isLoading ||
              (tabs.current === "PREDICTIONS" && getPrediction.isLoading) ||
              (tabs.current === "HISTORY_MATCHES" &&
                getHistoryMatches.isLoading) ||
              (tabs.current === "LIVE_MATCHES" && getLiveMatches.isLoading) ? (
                <Grid container justifyContent="center">
                  <CircularProgress />
                </Grid>
              ) : (
                <>
                  {!filteredData?.length ? (
                    <>
                      <TabPanel
                        value={tabsEnum.HISTORY_MATCHES}
                        sx={{ padding: 0 }}
                      >
                        <Matches
                          gameAnalysis={gameAnalysis}
                          gamesPages={
                            getHistoryMatches.data || {
                              pages: [[]],
                              pageParams: [1],
                            }
                          }
                          grabStatistics={grabStatistics}
                          useInfiniteQuery
                        />
                      </TabPanel>
                      <TabPanel
                        value={tabsEnum.LIVE_MATCHES}
                        sx={{ padding: 0 }}
                      >
                        <Matches
                          gameAnalysis={gameAnalysis}
                          gamesData={getLiveMatches.data || []}
                          grabStatistics={grabStatistics}
                        />
                      </TabPanel>
                      <TabPanel
                        value={tabsEnum.PREDICTIONS}
                        sx={{ padding: 0 }}
                      >
                        <Matches
                          gameAnalysis={gameAnalysis}
                          gamesPages={
                            getPrediction.data || {
                              pages: [[]],
                              pageParams: [1],
                            }
                          }
                          grabStatistics={grabStatistics}
                          removeDetails
                          useInfiniteQuery
                        />
                      </TabPanel>
                    </>
                  ) : (
                    <>
                      <Matches
                        gameAnalysis={gameAnalysis}
                        gamesData={filteredData || []}
                        grabStatistics={grabStatistics}
                      />
                    </>
                  )}
                </>
              )}
              <Grid item sm={12} display="flex" justifyContent="center">
                {getPrediction.isFetchingNextPage ||
                getHistoryMatches.isFetchingNextPage ? (
                  <CircularProgress />
                ) : tabs.current === "LIVE_MATCHES" ||
                  selectedLeague?.source ||
                  filteredData?.length ||
                  search ? null : (
                  //Load More Content
                  <div ref={ref}></div>
                )}
              </Grid>
            </S.GameBodyContainer>
          </TabContext>
        </Grid>
      </Grid>
    </S.HomeContainer>
  );
}
